import {normalizeScopeKey} from '@/matic/features/agents/otto/views/hooks/useView';
import {dedupeStems, sanitizeShortcutStem} from './shortcut-filenames';
import {
  renderShortcutFile,
  shortcutFileExtension,
  type ShortcutFormat,
} from './shortcut-file-formats';
import {
  isBuiltinDefinitionId,
  libraryRootShortcutUrl,
  pageShortcutUrl,
  viewShortcutUrl,
} from './shortcut-urls';

/**
 * Turns Pages (and optionally their generated Views) into a flat list of files
 * to write. Pure and writer-agnostic: it never touches the filesystem, the
 * network, or React, so the same manifest could be written to a directory
 * handle today and packed into a zip later without changing this file.
 *
 * Name resolution for Views is *injected* rather than fetched, for the same
 * reason.
 */

/** A directory path relative to the folder we own, plus the file to put in it. */
export type ShortcutManifestEntry = {
  dir: string[];
  filename: string;
  contents: string;
};

/** Stable identity for a generated View shortcut across selection and export. */
export function shortcutViewKey(
  scopeKey: string,
  definitionId: string
): string {
  return `${scopeKey}\u0000${definitionId}`;
}

/** Minimal shape of a pin — structural, so this module doesn't couple to the API type. */
type ManifestPin = {
  id: string;
  label: string;
  createdAt: string;
  payload: {refs: Array<{objectTypeId: string; objectRecordId: string}>};
};

/** Minimal shape of an already-generated View instance. */
type ManifestViewInstance = {
  scopeKey: string;
  definitionId: string;
};

export type BuildShortcutManifestInput = {
  origin: string;
  pins: ManifestPin[];
  format: ShortcutFormat;
  selectedViewKeys: ReadonlySet<string>;
  /**
   * Only instances that have actually been generated. Opening a View that has
   * never been generated kicks off a real, billed LLM run on arrival — a
   * shortcut is not an inert link, so this list must never be widened to "every
   * Definition available in this scope".
   *
   * A *stale* artifact is safe: `get_view.py` serves the cache with
   * `stale=True` and zero LLM, and regeneration is always an explicit
   * `force_regenerate`. The one exception is a `spec_version` bump, which does
   * regenerate on next read — but that cost lands on the first visit through
   * any route, so a shortcut is no worse than clicking through the app.
   */
  viewInstances: ManifestViewInstance[];
  /** Display name per Definition id, including the two built-ins. */
  definitionLabels: Map<string, string>;
};

/**
 * The folder both writers put everything inside.
 *
 * For the directory-picker path this is created inside whatever the user
 * chooses, and owning it by construction is what makes pruning safe. For the
 * zip path it's the archive's single root, so unpacking yields one folder
 * rather than scattering loose files across a Desktop.
 */
export const SHORTCUTS_FOLDER_NAME = 'MATIC Pages';

/**
 * Subfolder holding View shortcuts, so they never collide with Page names.
 * Exported so the writer's prune pass can recognize it as ours regardless of
 * whether the *current* manifest wants it populated — see
 * `OWNED_SUBDIRECTORY_NAMES` in `file-system-writer.ts`.
 */
export const VIEWS_DIR = 'Views';

/** The Library root shortcut, always present — a working door even in a stale folder. */
const ROOT_LABEL = 'Library';

function scopeKeyForPin(pin: ManifestPin): string {
  return normalizeScopeKey(
    pin.payload.refs.map((ref) => `${ref.objectTypeId}:${ref.objectRecordId}`)
  );
}

export function buildShortcutManifest({
  origin,
  pins,
  format,
  selectedViewKeys,
  viewInstances,
  definitionLabels,
}: BuildShortcutManifestInput): ShortcutManifestEntry[] {
  const extension = shortcutFileExtension(format);
  const file = (
    dir: string[],
    stem: string,
    url: string
  ): ShortcutManifestEntry => ({
    dir,
    filename: `${stem}${extension}`,
    contents: renderShortcutFile(url, format),
  });

  // Oldest-first so adding a Page never renumbers files already on disk.
  const ordered = [...pins].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );

  // The root shortcut shares a directory with the Pages, so it shares their
  // dedupe namespace — a Page literally named "Library" gets suffixed, not
  // silently merged with the root link.
  const rootStems = dedupeStems([
    ROOT_LABEL,
    ...ordered.map((pin) => sanitizeShortcutStem(pin.label)),
  ]);

  const entries: ShortcutManifestEntry[] = [
    file([], rootStems[0], libraryRootShortcutUrl(origin)),
  ];

  for (const [index, pin] of ordered.entries()) {
    entries.push(
      file([], rootStems[index + 1], pageShortcutUrl(origin, pin.payload.refs))
    );
  }

  const instancesByScopeKey = new Map<string, ManifestViewInstance[]>();
  for (const instance of viewInstances) {
    const existing = instancesByScopeKey.get(instance.scopeKey) ?? [];
    existing.push(instance);
    instancesByScopeKey.set(instance.scopeKey, existing);
  }

  const viewStems: string[] = [];
  const viewUrls: string[] = [];

  for (const [index, pin] of ordered.entries()) {
    // No manual cap check here: `viewInstances` only ever contains scope keys
    // the backend actually generated, and the backend rejects any scope over
    // its own MAX_SCOPE_ITEMS before persisting an artifact (`scope_key.py`'s
    // `parse_scope_refs`). An over-cap pin's scopeKey simply won't be a key in
    // `instancesByScopeKey`, so the lookup below naturally yields nothing —
    // correct by construction, not by a client-side guard that could drift
    // from the server's real cap.
    const scopeKey = scopeKeyForPin(pin);
    for (const instance of instancesByScopeKey.get(scopeKey) ?? []) {
      // The Page's own shortcut already opens its Brief/Portfolio.
      if (isBuiltinDefinitionId(instance.definitionId)) continue;
      if (
        !selectedViewKeys.has(shortcutViewKey(scopeKey, instance.definitionId))
      ) {
        continue;
      }

      const definitionLabel = definitionLabels.get(instance.definitionId);
      if (!definitionLabel) continue;

      viewStems.push(
        sanitizeShortcutStem(`${rootStems[index + 1]} — ${definitionLabel}`)
      );
      viewUrls.push(viewShortcutUrl(origin, scopeKey, instance.definitionId));
    }
  }

  const dedupedViewStems = dedupeStems(viewStems);
  for (const [index, stem] of dedupedViewStems.entries()) {
    entries.push(file([VIEWS_DIR], stem, viewUrls[index]));
  }

  return entries;
}
